import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import { FileText, Wallet, Clock, Building2, Users, Receipt, ArrowUpRight, TrendingDown } from 'lucide-react';
import { SkeletonDashboard } from '../components/Skeleton';
import FYPicker from '../components/FYPicker';
import { getCurrentFY } from '../utils/financialYear';

const Dashboard = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFY, setSelectedFY] = useState(getCurrentFY());

  useEffect(() => {
    fetchReportData();
  }, [selectedFY]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedFY) params.fy = selectedFY;
      const response = await reportsAPI.getPendingAmounts(params);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SkeletonDashboard />;

  const quickStats = [
    {
      title: 'Total Bills',
      value: reportData?.summary?.totalBills || 0,
      icon: FileText,
      iconBg: 'bg-blue-100 text-blue-600',
      link: '/bills'
    },
    {
      title: 'Total Payments',
      value: reportData?.summary?.totalPayments || 0,
      icon: Wallet,
      iconBg: 'bg-emerald-100 text-emerald-600',
      link: '/payments'
    },
    {
      title: 'Pending Amount',
      value: reportData?.summary?.pendingAmount || 0,
      icon: Clock,
      iconBg: reportData?.summary?.pendingAmount > 0 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500',
      link: '/reports'
    }
  ];

  const quickActions = [
    { title: 'Add Company', desc: 'New company', icon: Building2, link: '/companies', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
    { title: 'Add Party', desc: 'New party', icon: Users, link: '/parties', color: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
    { title: 'Create Bill', desc: 'New bill', icon: FileText, link: '/bills', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
    { title: 'Record Payment', desc: 'New payment', icon: Receipt, link: '/payments', color: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of your billing and payments</p>
        </div>
        <FYPicker value={selectedFY} onChange={setSelectedFY} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="group bg-white rounded-2xl border border-brand-100 p-5 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5 transition-all duration-200"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.iconBg}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-xl font-bold text-brand-900 tabular-nums">
                    ₹{stat.value.toLocaleString('en-IN')}
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-brand-400 transition-colors" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-brand-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link
                key={index}
                to={action.link}
                className={`flex flex-col items-center gap-2.5 p-5 rounded-2xl border border-brand-100 bg-white hover:shadow-md transition-all duration-200`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${action.color} transition-colors`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-brand-900">{action.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{action.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Breakdown Tables */}
      {reportData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-brand-100 p-5">
            <h3 className="text-base font-semibold text-brand-900 mb-4">Top Companies by Pending</h3>
            {reportData.companyBreakdown && reportData.companyBreakdown.filter(c => c.pendingAmount > 0).length > 0 ? (
              <div className="space-y-3">
                {reportData.companyBreakdown
                  .filter(c => c.pendingAmount > 0)
                  .sort((a, b) => b.pendingAmount - a.pendingAmount)
                  .slice(0, 5)
                  .map((company, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center text-xs font-bold text-brand-500">
                          {company.companyName?.charAt(0)}
                        </div>
                        <span className="text-sm font-medium text-brand-900">{company.companyName}</span>
                      </div>
                      <span className="text-sm font-semibold text-red-600 tabular-nums">
                        ₹{company.pendingAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 text-gray-400">
                <TrendingDown className="w-8 h-8 mb-2" />
                <p className="text-sm">No pending amounts</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-brand-100 p-5">
            <h3 className="text-base font-semibold text-brand-900 mb-4">Top Parties by Pending</h3>
            {reportData.partyBreakdown && reportData.partyBreakdown.filter(p => p.pendingAmount > 0).length > 0 ? (
              <div className="space-y-3">
                {reportData.partyBreakdown
                  .filter(p => p.pendingAmount > 0)
                  .sort((a, b) => b.pendingAmount - a.pendingAmount)
                  .slice(0, 5)
                  .map((party, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-xs font-bold text-emerald-600">
                          {party.partyName?.charAt(0)}
                        </div>
                        <span className="text-sm font-medium text-brand-900">{party.partyName}</span>
                      </div>
                      <span className="text-sm font-semibold text-red-600 tabular-nums">
                        ₹{party.pendingAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 text-gray-400">
                <TrendingDown className="w-8 h-8 mb-2" />
                <p className="text-sm">No pending amounts</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
